import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useRef, useState } from "react"
import { FiCheck, FiChevronDown, FiPlus, FiSearch } from "react-icons/fi"
import { type UserPublic, UsersService } from "../../../client"

type Props = {
  value?: string
  onChange: (nextValue: string) => void
  size?: "sm" | "md"
  isDisabled?: boolean
  placeholder?: string
}

export default function AccountManagerSelect({
  value = "",
  onChange,
  size = "sm",
  isDisabled = false,
  placeholder = "Wybierz opiekuna konta…",
}: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [search, setSearch] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])

  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const activeBg = useColorModeValue("teal.50", "teal.900")
  const activeColor = useColorModeValue("teal.700", "teal.200")
  const borderColor = useColorModeValue("gray.200", "gray.700")

  const { data: usersData, isPending } = useQuery({
    queryKey: ["users", "list-all"],
    queryFn: () => UsersService.readUsers({ limit: 500, skip: 0 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  })

  const users = usersData?.data ?? []

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users
    const query = search.trim().toLowerCase()
    return users.filter((u) => {
      const name = (u.fullName ?? "").toLowerCase()
      const email = (u.email ?? "").toLowerCase()
      return name.includes(query) || email.includes(query)
    })
  }, [users, search])

  const handleSelect = (nextValue: string) => {
    onChange(nextValue)
    onClose()
    setSearch("")
  }

  const isCustomValue =
    Boolean(search.trim()) &&
    !users.some(
      (u) =>
        (u.fullName ?? "").toLowerCase() === search.trim().toLowerCase() ||
        (u.email ?? "").toLowerCase() === search.trim().toLowerCase(),
    )

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      initialFocusRef={searchInputRef}
      placement="bottom-start"
      matchWidth
    >
      <PopoverTrigger>
        <Button
          size={size}
          variant="outline"
          fontWeight="normal"
          justifyContent="space-between"
          w="100%"
          isDisabled={isDisabled}
          rightIcon={<FiChevronDown />}
          borderColor={isInvalidValue(value) ? "red.500" : undefined}
        >
          <Text noOfLines={1} textAlign="left">
            {value.trim() ? value : placeholder}
          </Text>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        w="100%"
        minW="280px"
        maxW="360px"
        p={2}
        borderRadius="md"
        boxShadow="lg"
        borderColor={borderColor}
      >
        <Stack spacing={2}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray" />
            </InputLeftElement>
            <Input
              ref={searchInputRef}
              placeholder="Szukaj po nazwisku lub emailu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          {/* Scrollable list box with fixed max height and internal scrollbar */}
          <Box
            maxH="220px"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="md"
            borderColor={borderColor}
            p={1}
            sx={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "gray.300",
                borderRadius: "3px",
              },
            }}
          >
            {/* Clear option */}
            <Box
              px={2}
              py={1.5}
              borderRadius="sm"
              cursor="pointer"
              fontSize="sm"
              color="gray.500"
              _hover={{ bg: hoverBg }}
              onClick={() => handleSelect("")}
            >
              <HStack justify="space-between">
                <Text fontStyle="italic">— Brak opiekuna —</Text>
                {!value && <FiCheck color="teal" />}
              </HStack>
            </Box>

            {isPending ? (
              <HStack justify="center" py={4}>
                <Spinner size="sm" />
              </HStack>
            ) : (
              <>
                {filteredUsers.map((user) => {
                  const displayName = user.fullName || user.email
                  const isSelected =
                    value.trim() === displayName.trim() ||
                    value.trim() === user.email.trim()
                  const isCurrent =
                    currentUser?.email === user.email ||
                    (currentUser?.fullName &&
                      currentUser?.fullName === user.fullName)

                  return (
                    <Box
                      key={user.id || user.ID || user.email}
                      px={2}
                      py={1.5}
                      borderRadius="sm"
                      cursor="pointer"
                      fontSize="sm"
                      bg={isSelected ? activeBg : undefined}
                      color={isSelected ? activeColor : undefined}
                      _hover={{ bg: isSelected ? activeBg : hoverBg }}
                      onClick={() => handleSelect(displayName)}
                    >
                      <HStack justify="space-between" spacing={2}>
                        <Box overflow="hidden" textOverflow="ellipsis">
                          <Text fontWeight={isSelected ? "semibold" : "normal"}>
                            {user.fullName || user.email}
                          </Text>
                          {user.fullName && (
                            <Text fontSize="xs" color="gray.500">
                              {user.email}
                            </Text>
                          )}
                        </Box>
                        <HStack spacing={1}>
                          {isCurrent && (
                            <Badge colorScheme="purple" fontSize="2xs">
                              Ty
                            </Badge>
                          )}
                          {isSelected && <FiCheck color="teal" />}
                        </HStack>
                      </HStack>
                    </Box>
                  )
                })}

                {filteredUsers.length === 0 && !isCustomValue && (
                  <Box p={3} textAlign="center" fontSize="xs" color="gray.500">
                    Brak wyników wyszukiwania
                  </Box>
                )}

                {/* Option to select typed custom text */}
                {isCustomValue && (
                  <Box
                    px={2}
                    py={1.5}
                    mt={1}
                    borderTopWidth="1px"
                    borderRadius="sm"
                    cursor="pointer"
                    fontSize="sm"
                    color="teal.600"
                    _hover={{ bg: hoverBg }}
                    onClick={() => handleSelect(search.trim())}
                  >
                    <HStack spacing={2}>
                      <FiPlus />
                      <Text noOfLines={1}>Użyj: "{search.trim()}"</Text>
                    </HStack>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Stack>
      </PopoverContent>
    </Popover>
  )
}

function isInvalidValue(_val: string): boolean {
  return false
}
